// web/src/admin/StartGoLiveButton.tsx — KC's trigger for the go-live run (portal spec 14 / D2), placed by spec 22 in the
// Lifecycle queue's "Ready to go live" block and on the client page. Only for sold/finishing sites; disabled until
// account, plan and nameservers are done. Confirm: "Nameservers confirmed with the client?" (KC changes them on a call).
// Without `ready`/`stage` it reads spec 14's onboarding itself (the client page).
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { SiteStage } from '../lib/types';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { toast } from '../components/Toast';
import { apiErrorText } from '../lib/errors';

export function StartGoLiveButton({ slug, ready, stage }: { slug: string; ready?: boolean; stage?: SiteStage }) {
  const qc = useQueryClient();
  const own = ready === undefined || stage === undefined;
  const q = useQuery({ queryKey: ['admin-onboarding', slug], queryFn: () => api.adminOnboarding(slug), enabled: own, refetchInterval: 30_000 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isReady = ready ?? q.data?.ready ?? false;
  const st = stage ?? q.data?.stage;
  if (st !== 'sold' && st !== 'finishing') return null;
  const go = async () => {
    setBusy(true);
    try {
      await api.adminStartGoLive(slug);
      toast({ kind: 'success', message: 'Go-live started. The go-live brain picks it up next.' });
      void qc.invalidateQueries();
    } catch (e) {
      toast({ kind: 'error', message: apiErrorText(e, 'Couldn’t start go-live.') });
    } finally { setBusy(false); setOpen(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} disabled={busy || !isReady} title={isReady ? undefined : 'Account, plan and nameservers first'}
        className="inline-flex min-h-11 items-center rounded-md bg-run px-4 text-sm font-medium text-runink disabled:opacity-50">Start go-live</button>
      <ConfirmDialog open={open} title="Nameservers confirmed with the client?"
        body={`Start go-live for ${slug}: the site moves to Going live and the go-live brain gets its task.`}
        confirmLabel={busy ? 'Starting…' : 'Start go-live'} onConfirm={() => void go()} onCancel={() => setOpen(false)} />
    </>
  );
}
